import { useEffect, useState } from 'react';
import {
  CampaignDetailModel,
  FormType,
  useCompleteCampaignStep,
  useGetUploadedFileLink,
} from '@urbanx/agx.campaigns.client';
import { StakeholderType } from '@urbanx/agx-ui-components';
import {
  AgxButton,
  AgxSpacer,
  AgxTypography,
  useToast,
  useWindowSize,
  Breakpoints,
} from '@urbanx/agx.ui-components2';
import placeHolderImage from 'assets/images/placeholder-property.png';
import { useAppSelector } from 'hooks/useAppSelector';
import { useFormLoader } from 'hooks/useFormLoader';
import { useSelectedContract } from 'hooks/useSelectedContract';
import { cleanTwoLineAddress } from 'utils/formatAddress';
import { formatListOfStrings } from 'utils/formatString';
import { getAllBuyerNames, prepareSignatories } from 'utils/vendorBuyerUtil';
import { formatCurrencyWithConditionalDecimals } from 'utils/formatNumber';
import { Tabs } from 'types/Tabs';
import { openFileInNewTab } from 'utils/openFileInNewTab';
import {
  ContractFileList,
  contractSubmissionErrorMessage,
  createFileDownloadOptions,
  getContractStageLabel,
  listDetailSeparator,
  withCampaignFormConfig,
} from './ContractSubmitPanel.helpers';

interface ContractSubmitPanelProps {
  campaign: CampaignDetailModel;
  currentTab: Tabs;
  showDownload?: boolean;
  showSubmit?: boolean;
}

const ContractSubmitPanel = ({
  campaign,
  showDownload = true,
  showSubmit = false,
}: ContractSubmitPanelProps) => {
  const contract = useSelectedContract();
  const { loadFormWithConfig } = useFormLoader();
  const { pushToast } = useToast();
  const availableForms = useAppSelector((state) => state.forms.availableForms);
  const [downloadFilePath, setDownloadFilePath] = useState<string>();
  const { data: downloadFileLink, isError: downloadFileLinkError } = useGetUploadedFileLink(
    downloadFilePath ?? '',
    !!downloadFilePath
  );
  const { completeCampaignStep, completeCampaignStepPending } = useCompleteCampaignStep({
    onSuccess: () => {},
    onError: () => {},
  });
  const { contractStage: stage, buyers, documents, salePrice } = contract || {};
  const { address, state, propertyImageUrls: { medium: imageUrl = null } = {} } = campaign;

  if (!stage) throw new Error('Contract stage is undefined');

  const contractStage = getContractStageLabel(state, stage);

  const [addressLineOne, addressLineTwo] = cleanTwoLineAddress(address);

  const allBuyers = prepareSignatories(buyers, StakeholderType.Buyer);

  const allBuyerNames = getAllBuyerNames(allBuyers);

  const screenSize = useWindowSize();

  const isDesktop = screenSize === Breakpoints.Desktop;

  useEffect(() => {
    if (!downloadFilePath || !downloadFileLink) return;

    openFileInNewTab(isDesktop, downloadFileLink);
    setDownloadFilePath(undefined);
  }, [downloadFilePath, downloadFileLink]);

  useEffect(() => {
    if (!downloadFileLinkError) return;

    setDownloadFilePath(undefined);
  }, [downloadFileLinkError]);

  const beginSubmission = async () => {
    const contractSubmissionForm = availableForms.find(
      (form) =>
        form.type === FormType.ContractSubmission &&
        form.state?.toString() === campaign.state?.toString()
    );

    if (!contractSubmissionForm || !campaign.campaignId || !contract?.contractId) return;

    try {
      const newForm = await completeCampaignStep({
        campaignId: campaign.campaignId,
        forcedCompletion: true,
        formId: contract.contractId,
      });

      if (!newForm) {
        pushToast({
          type: 'error',
          message: contractSubmissionErrorMessage,
        });
        return;
      }

      await loadFormWithConfig(withCampaignFormConfig(newForm));
    } catch (err) {
      console.error(err);
      pushToast({
        type: 'error',
        message: contractSubmissionErrorMessage,
      });
    }
  };

  const fileDownloadOptions = createFileDownloadOptions(documents, setDownloadFilePath);

  return (
    <AgxSpacer fill contentAlignment="center" gap="spacing-xl">
      <AgxTypography
        id="contract-submit-panel-address"
        type="heading"
        size="2x-large"
        style={{ textAlign: 'center' }}
      >
        {addressLineOne},
        <br />
        {addressLineTwo}
      </AgxTypography>
      <AgxTypography
        id="contract-submit-panel-buyers-sale-price"
        type="body"
        size="medium"
        style={{ textAlign: 'center' }}
      >
        {formatListOfStrings(allBuyerNames)} {listDetailSeparator}{' '}
        {formatCurrencyWithConditionalDecimals(salePrice ?? 0, 2)}
      </AgxTypography>
      <AgxSpacer
        direction="horizontal"
        contentAlignment="center"
        contentJustification="center"
        className="listing-imageCard"
      >
        <div
          style={{
            backgroundImage: `url("${imageUrl || placeHolderImage}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            aspectRatio: '2/1',
          }}
          className={`imageCard-image`}
        >
          <div className="image-tag">
            <AgxTypography
              id="contract-submit-panel-stage"
              type="label"
              size="medium"
              color="var(--brand-text-button-color)"
            >
              {contractStage}
            </AgxTypography>
          </div>
        </div>
      </AgxSpacer>
      <ContractFileList
        documents={documents}
        showDownload={showDownload}
        state={state}
        fileDownloadOptions={fileDownloadOptions}
      />
      {showSubmit && (
        <AgxButton
          id="startsubmission"
          variant="primary"
          size="large"
          text="Submit Contract"
          onClick={beginSubmission}
          disabled={completeCampaignStepPending}
        />
      )}
    </AgxSpacer>
  );
};

export default ContractSubmitPanel;
